import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { FieldErrors } from '../components/FieldErrors.jsx';
import { FormInput } from '../components/FormControls.jsx';

const EMPTY_DRAFT = {
  id: null,
  name: '',
  description: '',
  base_role: 'team_member',
  permissions: [],
  is_system: false,
};

export function AdminTeamAccessPage() {
  const [catalog, setCatalog] = useState([]);
  const [presets, setPresets] = useState([]);
  const [locked, setLocked] = useState(null);
  const [roles, setRoles] = useState([]);
  const [members, setMembers] = useState([]);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [query, setQuery] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [memberId, setMemberId] = useState(null);
  const [memberRoleId, setMemberRoleId] = useState('');
  const [memberOverrides, setMemberOverrides] = useState({});
  const [memberQuery, setMemberQuery] = useState('');
  const [savingMember, setSavingMember] = useState(false);

  const load = async () => {
    const data = await apiJson('/api/admin/team/access');
    setCatalog(data.catalog || []);
    setPresets(data.presets || []);
    setLocked(data.locked || null);
    setRoles(data.roles || []);
    setMembers(data.members || []);
    return data;
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load()
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedMember = members.find((member) => member.id === memberId) || null;

  const visibleGroups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return catalog
      .map((group) => ({
        ...group,
        permissions: group.permissions.filter((permission) => {
          if (!needle) return true;
          return (
            permission.label.toLowerCase().includes(needle)
            || permission.description.toLowerCase().includes(needle)
            || permission.key.toLowerCase().includes(needle)
          );
        }),
      }))
      .filter((group) => group.permissions.length > 0);
  }, [catalog, query]);

  const startNew = (preset) => {
    setDraft({
      id: null,
      name: preset && preset.id !== 'empty' ? preset.name : '',
      description: preset?.description || '',
      base_role: preset?.base_role || 'team_member',
      permissions: [...(preset?.permissions || [])],
      is_system: false,
    });
    setFieldErrors(undefined);
    setError('');
    setNotice('');
  };

  const editRole = (role) => {
    setDraft({
      id: role.id,
      name: role.name,
      description: role.description || '',
      base_role: role.base_role,
      permissions: [...(role.permissions || [])],
      is_system: Boolean(role.is_system),
    });
    setFieldErrors(undefined);
    setError('');
  };

  const togglePermission = (key) => {
    setDraft((current) => {
      const has = current.permissions.includes(key);
      return {
        ...current,
        permissions: has ? current.permissions.filter((item) => item !== key) : [...current.permissions, key],
      };
    });
  };

  const toggleGroup = (group, on) => {
    const keys = group.permissions.map((permission) => permission.key);
    setDraft((current) => {
      const rest = current.permissions.filter((key) => !keys.includes(key));
      return { ...current, permissions: on ? [...rest, ...keys] : rest };
    });
  };

  const saveRole = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');
    setFieldErrors(undefined);
    const body = {
      name: draft.name,
      description: draft.description,
      base_role: draft.base_role,
      permissions: draft.permissions,
    };
    try {
      if (draft.id) {
        await apiJson(`/api/admin/team/roles/${draft.id}`, { method: 'PUT', body });
        setNotice('Role saved. People on this role pick it up the next time they load the portal.');
      } else {
        const created = await apiJson('/api/admin/team/roles', { method: 'POST', body });
        setDraft((current) => ({ ...current, id: created.id, is_system: false }));
        setNotice('Role created. Assign it to someone below.');
      }
      await load();
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.body?.errors);
    } finally {
      setSaving(false);
    }
  };

  const removeRole = async (role) => {
    if (!window.confirm(`Delete the role “${role.name}”? People on it return to built-in access.`)) {
      return;
    }
    setError('');
    try {
      await apiJson(`/api/admin/team/roles/${role.id}`, { method: 'DELETE' });
      if (draft.id === role.id) setDraft(EMPTY_DRAFT);
      setNotice('Role deleted.');
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const openMember = (member) => {
    const overrides = {};
    for (const override of member.overrides || []) {
      overrides[override.permission] = override.effect;
    }
    setMemberId(member.id);
    setMemberRoleId(member.organization_role_id ? String(member.organization_role_id) : '');
    setMemberOverrides(overrides);
    setMemberQuery('');
    setError('');
  };

  const saveMember = async () => {
    if (!selectedMember) return;
    setSavingMember(true);
    setError('');
    setNotice('');
    try {
      await apiJson(`/api/admin/team/members/${selectedMember.id}/access`, {
        method: 'PUT',
        body: {
          organization_role_id: memberRoleId ? Number(memberRoleId) : null,
          overrides: Object.entries(memberOverrides)
            .filter(([, effect]) => effect === 'allow' || effect === 'deny')
            .map(([permission, effect]) => ({ permission, effect })),
        },
      });
      setNotice(`Access updated for ${selectedMember.name}.`);
      setMemberId(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingMember(false);
    }
  };

  const memberGroups = useMemo(() => {
    const needle = memberQuery.trim().toLowerCase();
    return catalog
      .map((group) => ({
        ...group,
        permissions: group.permissions.filter((permission) => {
          if (!needle) return true;
          return permission.label.toLowerCase().includes(needle) || permission.key.toLowerCase().includes(needle);
        }),
      }))
      .filter((group) => group.permissions.length > 0);
  }, [catalog, memberQuery]);

  return (
    <div className="stack access-page">
      <div className="row-actions" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <p className="muted small" style={{ margin: 0 }}>
            <Link to="/admin/team">Team</Link>
          </p>
          <h2 style={{ margin: '0.15rem 0 0' }}>Roles and permissions</h2>
        </div>
        <button type="button" className="btn-primary" onClick={() => startNew(null)}>
          New role
        </button>
      </div>
      <p className="muted">
        Build roles for the company, then assign a role to each person. A person can also have exceptions that allow or
        deny a single permission. Billing and this page stay with the company owner.
      </p>
      {locked ? (
        <ul className="muted small" style={{ marginTop: 0 }}>
          <li>{locked.billing}</li>
          <li>{locked.roles}</li>
        </ul>
      ) : null}
      {notice ? <p className="muted">{notice}</p> : null}
      {error ? <p className="err">{error}</p> : null}
      {loading ? <p className="muted">Loading…</p> : null}

      {!loading ? (
        <div className="access-layout">
          <aside className="access-roles">
            <h3>Roles</h3>
            <ul className="access-role-list">
              {roles.map((role) => (
                <li key={role.id}>
                  <button
                    type="button"
                    className={draft.id === role.id ? 'access-role active' : 'access-role'}
                    onClick={() => editRole(role)}
                  >
                    <strong>{role.name}</strong>
                    <span className="muted small">
                      {role.is_system ? 'Built-in · ' : ''}
                      {role.base_role === 'manager' ? 'Team admin seat' : 'Team member seat'}
                      {' · '}
                      {role.users_count || 0} people
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <h3>Start from</h3>
            <div className="stack" style={{ gap: '0.4rem' }}>
              {presets.map((preset) => (
                <button key={preset.id} type="button" className="btn btn-outline-secondary btn-sm" onClick={() => startNew(preset)}>
                  {preset.name}
                </button>
              ))}
            </div>
          </aside>

          <form className="access-editor stack" onSubmit={saveRole}>
            <h3>{draft.id ? 'Edit role' : 'New role'}</h3>
            <label className="field">
              <span>Name</span>
              <FormInput value={draft.name} onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))} required />
            </label>
            <label className="field">
              <span>Description</span>
              <textarea
                value={draft.description}
                onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
                rows={2}
              />
            </label>
            <label className="field">
              <span>Seat</span>
              <select
                value={draft.base_role}
                onChange={(event) => setDraft((current) => ({ ...current, base_role: event.target.value }))}
              >
                <option value="manager">Team admin seat</option>
                <option value="team_member">Team member seat</option>
              </select>
              <span className="muted small">
                The seat is the account type stored on the person. Task visibility still follows “View all tasks”. Client
                assignments still apply unless the role has “See every client”.
              </span>
            </label>
            <label className="field">
              <span>Find a permission</span>
              <FormInput value={query} onChange={(event) => setQuery(event.target.value)} placeholder="clients, mail, delete…" />
            </label>
            <p className="muted small">{draft.permissions.length} permissions on</p>
            {visibleGroups.map((group) => {
              const keys = group.permissions.map((permission) => permission.key);
              const selected = keys.filter((key) => draft.permissions.includes(key)).length;
              return (
                <section key={group.id} className="access-group">
                  <div className="access-group-head">
                    <div>
                      <h4>{group.label}</h4>
                      <p className="muted small">{group.description}</p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => toggleGroup(group, selected !== keys.length)}
                    >
                      {selected === keys.length ? 'Clear group' : 'All in group'}
                    </button>
                  </div>
                  <ul className="access-permissions">
                    {group.permissions.map((permission) => (
                      <li key={permission.key}>
                        <label>
                          <input
                            type="checkbox"
                            checked={draft.permissions.includes(permission.key)}
                            onChange={() => togglePermission(permission.key)}
                          />
                          <span>
                            <strong>{permission.label}</strong>
                            <span className="muted small">{permission.description}</span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
            <FieldErrors errors={fieldErrors} />
            <div className="d-flex flex-wrap gap-2">
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Saving…' : draft.id ? 'Save role' : 'Create role'}
              </button>
              {draft.id && !draft.is_system ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    const role = roles.find((item) => item.id === draft.id);
                    if (role) void removeRole(role);
                  }}
                >
                  Delete role
                </button>
              ) : null}
            </div>
          </form>
        </div>
      ) : null}

      {!loading ? (
        <section className="stack">
          <h3>People</h3>
          <p className="muted small">
            Built-in access follows the Team admin or Team member role, including edits you make to those roles. Assign
            another role to replace that list. Exceptions override the role for one person.
          </p>
          {members.length === 0 ? <p className="muted">No team members yet.</p> : null}
          {members.length > 0 ? (
            <div className="table-wrap">
              <table className="table table-striped table-hover data-table mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Exceptions</th>
                    <th>Permissions</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => (
                    <tr key={member.id}>
                      <td>
                        {member.name}
                        <div className="muted small">{member.email}</div>
                      </td>
                      <td>{member.organization_role?.name || (member.role === 'team_member' ? 'Built-in team member' : 'Built-in team admin')}</td>
                      <td>{(member.overrides || []).length}</td>
                      <td>{(member.permissions || []).length}</td>
                      <td className="row-actions">
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => openMember(member)}>
                          Customize
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {selectedMember ? (
            <div className="access-member stack">
              <h3>Access for {selectedMember.name}</h3>
              <label className="field">
                <span>Role</span>
                <select value={memberRoleId} onChange={(event) => setMemberRoleId(event.target.value)}>
                  <option value="">
                    Built-in {selectedMember.role === 'team_member' ? 'team member' : 'team admin'}
                  </option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Find a permission</span>
                <FormInput value={memberQuery} onChange={(event) => setMemberQuery(event.target.value)} placeholder="Filter exceptions…" />
              </label>
              {memberGroups.map((group) => (
                <section key={group.id} className="access-group">
                  <h4>{group.label}</h4>
                  <ul className="access-overrides">
                    {group.permissions.map((permission) => (
                      <li key={permission.key}>
                        <span>{permission.label}</span>
                        <select
                          aria-label={`${permission.label} for ${selectedMember.name}`}
                          value={memberOverrides[permission.key] || 'inherit'}
                          onChange={(event) => {
                            const effect = event.target.value;
                            setMemberOverrides((current) => {
                              const next = { ...current };
                              if (effect === 'inherit') delete next[permission.key];
                              else next[permission.key] = effect;
                              return next;
                            });
                          }}
                        >
                          <option value="inherit">Inherit from role</option>
                          <option value="allow">Allow</option>
                          <option value="deny">Deny</option>
                        </select>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
              <div className="d-flex flex-wrap gap-2">
                <button type="button" className="btn-primary" disabled={savingMember} onClick={() => void saveMember()}>
                  {savingMember ? 'Saving…' : 'Save person'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setMemberId(null)}>
                  Close
                </button>
              </div>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
